import { useMemo } from "react";
import { Flame } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { CashFlowRow, DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import { formatCurrency } from "@/lib/currency";

type Props = {
  rows: CashFlowRow[];
  periodKeys: string[];
  displayCurrency: DisplayCurrency;
  isLoading: boolean;
};

function activityType(parentKey: string): { label: string; className: string } {
  if (parentKey.startsWith("op-")) return { label: "Operating", className: "bg-blue-100 text-blue-700 border-blue-200" };
  if (parentKey.startsWith("inv-")) return { label: "Investing", className: "bg-amber-100 text-amber-700 border-amber-200" };
  if (parentKey.startsWith("fin-")) return { label: "Financing", className: "bg-violet-100 text-violet-700 border-violet-200" };
  return { label: "Other", className: "bg-muted text-muted-foreground" };
}

export function TopExpenses({ rows, periodKeys, displayCurrency, isLoading }: Props) {
  const ranked = useMemo(() => {
    const expenseRows = rows.filter(
      (r) => r.key.includes("-exp-") && r.children && r.children.length > 0
    );

    const items: { label: string; total: number; activity: ReturnType<typeof activityType> }[] = [];

    for (const parent of expenseRows) {
      const activity = activityType(parent.key);
      for (const child of parent.children!) {
        let total = 0;
        for (const k of periodKeys) {
          total += child.values.get(k) ?? 0;
        }
        if (Math.abs(total) > 0) {
          items.push({ label: child.label, total, activity });
        }
      }
    }

    items.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    return items.slice(0, 10);
  }, [rows, periodKeys]);

  if (isLoading) {
    return <div className="h-48 rounded-xl bg-muted animate-pulse" />;
  }

  if (ranked.length === 0) return null;

  const maxAbs = Math.abs(ranked[0]?.total ?? 1);
  const fmt = (v: number) => formatCurrency(Math.abs(v), displayCurrency);

  return (
    <Card className="border-emerald-200/60 bg-gradient-to-br from-emerald-50/40 to-green-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Flame className="h-4 w-4 text-emerald-500" />
          Top Expenses
          <Badge variant="outline" className="text-[10px] font-normal ml-auto">
            Top {ranked.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {ranked.map((item, i) => {
          const pct = (Math.abs(item.total) / maxAbs) * 100;
          return (
            <div key={`${item.label}-${i}`} className="flex items-center gap-2 text-xs">
              <span className="w-5 text-right font-mono text-muted-foreground">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="font-medium truncate">{item.label}</span>
                  <span className={`text-[9px] px-1.5 py-0 rounded-full border ${item.activity.className}`}>
                    {item.activity.label}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-400"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <span className="font-mono font-semibold text-emerald-700 tabular-nums whitespace-nowrap">
                ({fmt(item.total)})
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
